import { useEffect, useMemo, useRef, useState } from 'react';
import { Command } from 'cmdk';
import {
  isValid,
  parse,
  previousDay,
  startOfMonth,
  startOfToday,
  startOfYesterday,
  sub,
} from 'date-fns';
import Fuse from 'fuse.js';
import { CalendarClock } from 'lucide-react';

import pluralize from '@utils/pluralize';
import {
  formatDatetime,
  getDateInTz,
  TimezoneFormat,
  useTimezoneFormat,
} from '@utils/timezone';

import css from './DateTimeInput.module.css';

type DateTimeInputProps = {
  value?: Date;
  onChange: (value: Date) => void;
};

function labelForValue(value: Date, timezoneFormat: TimezoneFormat) {
  return formatDatetime(value, timezoneFormat, "d MMM yyyy 'at' HH:mm");
}

export function DateTimeInput({ value, onChange }: DateTimeInputProps) {
  const container = useRef<HTMLDivElement>(null);
  const [timezoneFormat] = useTimezoneFormat();
  const [inputValue, setInputValue] = useState(
    value ? labelForValue(value, timezoneFormat) : ''
  );
  const [suggesting, setSuggesting] = useState(false);

  const label = useMemo(() => {
    if (!value) return undefined;
    return labelForValue(value, timezoneFormat);
  }, [timezoneFormat, value]);

  const handleChange = (date: Date) => {
    setInputValue(labelForValue(date, timezoneFormat) || '');
    onChange(date);
    setSuggesting(false);
  };

  const handleValueChange = (value: string) => {
    setInputValue(value);
    setSuggesting(true);
  };

  useEffect(() => {
    if (!suggesting) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (!container.current) return;
      if (container.current.contains(e.target as Node)) return;

      setSuggesting(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [suggesting]);

  const suggestions = suggesting
    ? getSuggestions(inputValue, timezoneFormat)
    : [];

  return (
    <div className={css.container} ref={container}>
      <Command shouldFilter={false}>
        <Command.Input
          className={css.input}
          onFocus={() => setSuggesting(true)}
          placeholder="Type a date or time..."
          value={suggesting ? inputValue : label}
          onValueChange={handleValueChange}
        />

        <Command.List className={css.list} data-active={suggestions.length > 0}>
          {suggesting && (
            <Suggestions suggestions={suggestions} onSelect={handleChange} />
          )}
        </Command.List>
      </Command>
    </div>
  );
}

function Suggestions({
  onSelect,
  suggestions,
}: {
  onSelect: (date: Date) => void;
  suggestions: Option[];
}) {
  return suggestions.map((option) => (
    <Option key={option.label} onSelect={() => onSelect(option.date)}>
      <CalendarClock />
      <span className={css.label}>{option.label}</span>
      <span className={css.sub}>{option.sub}</span>
    </Option>
  ));
}

function Option({
  children,
  onSelect,
}: {
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item className={css.option} onSelect={onSelect}>
      {children}
    </Command.Item>
  );
}

type Option = {
  date: Date;
  label: string;
  sub?: string;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function getMonth(search: string) {
  return MONTHS.find((m) => m.toLowerCase().startsWith(search.toLowerCase()));
}

function parseFormats(input: string, formats: string[]) {
  for (const format of formats) {
    try {
      const result = parse(input, format, new Date());
      if (isValid(result)) return result;
      continue;
    } catch {
      continue;
    }
  }

  return null;
}

// suggests values when the user enters just a time.
function time(search: string, timezone: TimezoneFormat): Option[] {
  const formats = [
    'h',
    'ha',
    'H',
    'h:mm',
    'H:',
    'Hmm',
    'H:mm',
    'h:mma',
    'h:mm a',
    'h:mm a',
    'hhmm',
  ];
  const date = parseFormats(search, formats);
  if (!date) return [];

  const tzDate = getDateInTz(date, timezone);

  return [dateToSuggestion(tzDate, timezone)];
}

function dateToSuggestion(
  date: Date,
  timezone: TimezoneFormat,
  options?: { label?: string; formatStr?: string }
) {
  const formatStr = options?.formatStr ?? "d MMM yyyy 'at' HH:mm";
  const sub = formatDatetime(date, timezone, formatStr);
  return {
    date,
    label: options?.label ?? sub,
    sub: options?.label ? sub : undefined,
  };
}

// takes a random input and tries to extract a time from it.
function extractTime(search: string, timezone: TimezoneFormat): string | null {
  const match = search.match(
    /\b(?<hours>\d{1,2}):?(?<minutes>\d{2})?(?<period>[ap]m)?/
  );
  if (!match?.groups) return null;

  const hours = match.groups.hours;
  const minutes = match.groups.minutes || '00';
  const period = match.groups.period;
  const date = period
    ? parse(`${hours}:${minutes}${period}`, 'h:mma', new Date())
    : parse(`${hours}:${minutes}`, 'H:mm', new Date());
  if (!isValid(date)) return null;

  const tzDate = getDateInTz(date, timezone);
  return formatDatetime(tzDate, timezone, 'h:mma');
}

// parse day month format.
function dayMonth(search: string, timezone: TimezoneFormat): Option[] {
  let match = search.match(
    /^(?<day>[0-9]{1,2})\s(?<month>[a-zA-Z]+)\s?(?<year>[0-9]{4}?(?=\s|$))?/
  );

  // try to parse as month day format.
  if (!match) {
    match = search.match(
      /^(?<month>[a-zA-Z]+)\s(?<day>[0-9]{1,2})\s?(?<year>[0-9]{4}?(?=\s|$))?/
    );
  }

  if (!match?.groups) return [];

  const day = match.groups.day;
  const month = getMonth(match.groups.month);
  const year = match.groups.year || new Date().getFullYear();
  const remaining = search.slice(match[0].length);
  const time = extractTime(remaining, timezone) || '12:00am';
  const parsedDate = parse(
    `${day} ${month} ${year} ${time}`,
    'd MMMM yyyy h:mma',
    new Date()
  );

  const date = dateInPast(parsedDate);

  if (!isValid(date)) return [];

  const tzDate = getDateInTz(date, timezone);
  return [dateToSuggestion(tzDate, timezone)];
}

// suggests values when the user enters just a number.
function justNumber(search: string, timezone: TimezoneFormat): Option[] {
  const match = search.match(/^(?<number>\d{1,2})\s?$/);
  if (!match?.groups) return [];
  const day = match.groups.number;
  const date = parse(`${day}`, 'd', new Date());
  return [dateToSuggestion(date, timezone)];
}

// The user is likely trying to enter a date in the past, so if the date is in the future, then change the year to the previous year
function dateInPast(date: Date): Date {
  const now = new Date();
  if (date > now) {
    date.setFullYear(date.getFullYear() - 1);
  }
  return date;
}

function justMonth(search: string, timezone: TimezoneFormat): Option[] {
  const month = getMonth(search);
  if (!search || !month) return [];

  const parsedDate = parse(`1 ${month}`, 'd MMMM', new Date());
  const dateAtMonthStart = startOfMonth(parsedDate);
  const date = dateInPast(dateAtMonthStart);

  return [dateToSuggestion(date, timezone)];
}

const UNITS = ['minutes', 'hours', 'days', 'weeks', 'months', 'years'];

function relativeDates(search: string, timezone: TimezoneFormat): Option[] {
  const match = search.match(
    /^(?<quantity>\d+)\s?(?<unit>[a-zA-Z]+)?\s?(ago|ag|a)?$/
  );
  if (!match?.groups) return [];

  const quantity = parseInt(match.groups.quantity);
  const unit = match.groups.unit;
  return UNITS.map((u) => {
    if (unit && !u.startsWith(unit)) return null;
    const date = sub(new Date(), { [u]: quantity });

    return dateToSuggestion(date, timezone, {
      label: pluralize(quantity, `${u.slice(0, -1)} ago`, `${u} ago`),
      formatStr: 'd MMM yyyy, HH:mm z',
    });
  }).filter(Boolean) as Option[];
}

const KEYWORDS = [
  {
    keyword: 'Now',
    value: () => new Date(),
  },
  {
    keyword: 'Today',
    value: () => startOfToday(),
  },
  {
    keyword: 'Yesterday',
    value: () => startOfYesterday(),
  },
  {
    keyword: 'Last Monday',
    value: () => previousDay(new Date(), 1),
  },
  {
    keyword: 'Last Tuesday',
    value: () => previousDay(new Date(), 2),
  },
  {
    keyword: 'Last Wednesday',
    value: () => previousDay(new Date(), 3),
  },
  {
    keyword: 'Last Thursday',
    value: () => previousDay(new Date(), 4),
  },
  {
    keyword: 'Last Friday',
    value: () => previousDay(new Date(), 5),
  },
  {
    keyword: 'Last Saturday',
    value: () => previousDay(new Date(), 6),
  },
  {
    keyword: 'Last Sunday',
    value: () => previousDay(new Date(), 0),
  },
];

const keywords = new Fuse(KEYWORDS, {
  keys: ['keyword'],
  threshold: 0.1,
  minMatchCharLength: 2,
});

function keyword(search: string, timezone: TimezoneFormat): Option[] {
  if (!search) return [];

  const results = keywords.search(search);
  return results.map(({ item }) => {
    return dateToSuggestion(item.value(), timezone, {
      label: item.keyword,
      formatStr: "d MMM yyyy 'at' h:mmaaa",
    });
  });
}

const SUGGESTIONS = [
  justNumber,
  time,
  dayMonth,
  justMonth,
  relativeDates,
  keyword,
];

function getSuggestions(search: string, timezone: TimezoneFormat): Option[] {
  return SUGGESTIONS.flatMap((fn) => {
    try {
      return fn(search, timezone);
    } catch {
      return [];
    }
  });
}
